import React, { useState, useEffect } from "react";
import {
  Plus,
  Trash2,
  X,
  RefreshCw,
  Phone,
  Award,
  Calendar,
  Minus,
  Mail,
  ChevronRight,
} from "lucide-react";
import api, { ApiResponse } from "../../services/api";
import { toast } from "react-hot-toast";
import UserDetailsModal, {
  LoyaltyCustomer,
  LoyaltyLevelBadge,
} from "../../components/UserDetailsModal";


const LoyaltyManagement: React.FC = () => {
  const [customers, setCustomers] = useState<LoyaltyCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const customersPerPage = 25;

  // Existing add/remove modal states
  const [editingCustomer, setEditingCustomer] = useState<LoyaltyCustomer | null>(null);
  const [points, setPoints] = useState("");
  const [actionType, setActionType] = useState<"add" | "remove">("add");

  // User details modal (orders, manual points, compensation)
  const [selectedUser, setSelectedUser] = useState<LoyaltyCustomer | null>(null);

  useEffect(() => {
    fetchCustomers(currentPage);
  }, [currentPage]);

  const fetchCustomers = async (page = 1, search = "") => {
    try {
      setLoading(true);
      setError(null);
      const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
      const response = await api.get<ApiResponse<{ data: LoyaltyCustomer[]; total: number }>>(
        `/loyalty/users?page=${page}&limit=${customersPerPage}${searchParam}`
      );

      if (response.data.success) {
        const sorted = [...response.data.data.data].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
        setCustomers(sorted);
        setTotalPages(Math.ceil(response.data.data.total / customersPerPage));
      } else {
        setCustomers([]);
        setError("Failed to load loyalty customers.");
      }
    } catch {
      setError("Failed to load loyalty customers. Please try again.");
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePointsUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingCustomer) return;

    let pointsValue = parseFloat(points);
    if (isNaN(pointsValue) || pointsValue <= 0) {
      toast.error("Please enter a valid positive number of points.");
      return;
    }
    pointsValue = parseFloat(pointsValue.toFixed(2));

    try {
      setError(null);
      const endpoint = actionType === "add" ? "/loyalty/add" : "/loyalty/remove";
      await api.post<ApiResponse<LoyaltyCustomer>>(endpoint, {
        phoneNumber: editingCustomer.phoneNumber,
        points: pointsValue,
      });

      setShowModal(false);
      toast.success(`Points ${actionType === "add" ? "added" : "removed"} successfully!`);
      const delta = actionType === "add" ? pointsValue : -pointsValue;
      const newBalance = parseFloat(((editingCustomer.bonusWallet?.balance ?? 0) + delta).toFixed(2));
      setCustomers((prev) => prev.map((c) => c._id === editingCustomer._id ? { ...c, bonusWallet: { ...c.bonusWallet, balance: newBalance } } : c));
      if (selectedUser?._id === editingCustomer._id) {
        setSelectedUser((prev) => prev ? { ...prev, bonusWallet: { ...prev.bonusWallet, balance: newBalance } } : prev);
      }
      resetForm();
    } catch {
      setError("Failed to update points. Please try again.");
      toast.error("Failed to update points.");
    }
  };

  const handleToggleFlag = async (
    customer: LoyaltyCustomer,
    flag: "blackListForCash" | "blackListForCard"
  ) => {
    const newValue = !customer[flag];
    try {
      await api.patch(`/loyalty/users/${customer.phoneNumber}/flags`, { [flag]: newValue });
      setCustomers((prev) =>
        prev.map((c) => c._id === customer._id ? { ...c, [flag]: newValue } : c)
      );
      if (selectedUser?._id === customer._id) {
        setSelectedUser((prev) => prev ? { ...prev, [flag]: newValue } : prev);
      }
      toast.success(`${flag === "blackListForCash" ? "Cash" : "Card"} blacklist ${newValue ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Failed to update blacklist flag.");
    }
  };

  const handleDelete = async (phoneNumber: string) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this customer? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    try {
      setError(null);
      await api.delete(`/loyalty/${phoneNumber}`);
      fetchCustomers(currentPage);
      toast.success("Customer deleted successfully!");
    } catch {
      toast.error("Points already 0");
    }
  };

  const handleEdit = (customer: LoyaltyCustomer, type: "add" | "remove") => {
    setEditingCustomer(customer);
    setActionType(type);
    setPoints("");
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setPoints("");
    setActionType("add");
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let input = e.target.value;
    const cleaned = input.replace(/[^\d-]/g, "");
    const digits = cleaned.replace(/-/g, "").slice(0, 10);
    let formatted = "";
    const validManualFormat =
      /^\d{1,3}$|^\d{3}-\d{0,3}$|^\d{3}-\d{3}-\d{0,4}$/.test(cleaned);

    if (validManualFormat && cleaned === input.replace(/[^\d-]/g, "")) {
      formatted = cleaned;
    } else {
      if (digits.length > 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      } else if (digits.length > 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        formatted = digits;
      }
    }
    setSearchTerm(formatted);
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanPhoneNumber = searchTerm.replace(/-/g, "");
    setCurrentPage(1);
    fetchCustomers(1, cleanPhoneNumber);
  };

  const refreshData = () => {
    setSearchTerm("");
    setCurrentPage(1);
    fetchCustomers(1, "");
  };


  if (loading && customers.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="relative max-w-7xl mx-auto p-4 pt-0 lg:p-8 flex flex-col overflow-y-auto no-scrollbar h-screen">
      {/* ================= HEADER ================= */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm py-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-800">User List</h1>
        </div>

        {/* ================= SEARCH ================= */}
        <div className="rounded-lg mb-6">
          <label className="block mb-2 text-sm font-medium text-gray-700">
            Search by Phone Number
          </label>
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Phone
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={handlePhoneNumberChange}
                placeholder="Search by phone number (xxx-xxx-xxxx)"
                maxLength={12}
                className="pl-10 pr-4 py-2 w-full border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Search
            </button>
            <button
              type="button"
              onClick={refreshData}
              className="w-10 h-10 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition flex items-center justify-center"
              title="Show All Customers"
            >
              <RefreshCw size={18} />
            </button>
          </form>
        </div>
      </div>

      {/* ================= ERROR ================= */}
      {error && (
        <div className="bg-red-500 text-white p-3 rounded-md mb-4 text-center">{error}</div>
      )}

      {/* ================= CONTENT ================= */}
      <div className="flex-1 relative">
        {loading && customers.length > 0 && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-lg">
            <div className="flex items-center gap-3 bg-white px-4 py-2 rounded-lg shadow">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-sm text-gray-600">Loading...</span>
            </div>
          </div>
        )}
        {customers.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <p className="text-lg font-medium">No customers found</p>
              <p className="text-sm mt-1">
                {searchTerm
                  ? "Try searching with a different phone number"
                  : "No loyalty customers available"}
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {customers.map((customer) => (
              <div
                key={customer._id}
                className="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Card Header - Clickable to open orders */}
                <div
                  className="mb-4 cursor-pointer group"
                  onClick={() => setSelectedUser(customer)}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
                      {customer.name || "Unknown Customer"}
                    </h3>
                    <ChevronRight
                      size={16}
                      className="text-gray-400 group-hover:text-blue-600 mt-1 flex-shrink-0 transition-colors"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={14} className="text-gray-400" />
                      <span>{customer.phoneNumber}</span>
                    </div>
                    {customer.email && (
                      <div className="flex items-center gap-2 text-sm text-gray-400">
                        <Mail size={14} className="text-gray-400" />
                        <span>{customer.email}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Loyalty Section */}
                <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Award size={20} className="text-yellow-500" />
                      <span className="text-xl font-bold text-blue-600">
                        {customer.bonusWallet?.balance.toFixed(2) ?? 0}
                      </span>
                      <span className="text-sm text-gray-600">points</span>
                    </div>
                    {/* <LoyaltyLevelBadge level={customer.loyaltyLevel} /> */}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                    <Calendar size={12} />
                    <span>Joined {new Date(customer.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Blacklist flags */}
                <div className="mb-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">Blacklist Cash</span>
                    <button
                      onClick={() => handleToggleFlag(customer, "blackListForCash")}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${customer.blackListForCash ? "bg-red-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${customer.blackListForCash ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">Blacklist Card</span>
                    <button
                      onClick={() => handleToggleFlag(customer, "blackListForCard")}
                      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${customer.blackListForCard ? "bg-red-500" : "bg-gray-300"}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${customer.blackListForCard ? "translate-x-5" : "translate-x-1"}`}
                      />
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <button
                    onClick={() => handleDelete(customer.phoneNumber)}
                    className="w-full flex items-center justify-center gap-1 px-3 py-2 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition"
                  >
                    <Trash2 size={14} />
                    Delete User
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ================= PAGINATION ================= */}
      {totalPages > 1 && (
        <div className="py-4 flex justify-center">
          <div className="flex gap-2 items-center">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Prev
            </button>
            {(() => {
              const pages = [];
              const showEllipsis = totalPages > 7;
              if (!showEllipsis) {
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === i
                          ? "bg-blue-600 text-white"
                          : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {i}
                    </button>
                  );
                }
              } else {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(totalPages, currentPage + 2);
                if (startPage > 1) {
                  pages.push(
                    <button
                      key={1}
                      onClick={() => setCurrentPage(1)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === 1 ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      1
                    </button>
                  );
                  if (startPage > 2) {
                    pages.push(
                      <span key="ellipsis1" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                }
                for (let i = startPage; i <= endPage; i++) {
                  if (i !== 1 && i !== totalPages) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1 rounded transition ${
                          currentPage === i ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                }
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(
                      <span key="ellipsis2" className="px-2 py-1 text-gray-500">
                        ...
                      </span>
                    );
                  }
                  pages.push(
                    <button
                      key={totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                      className={`px-3 py-1 rounded transition ${
                        currentPage === totalPages ? "bg-blue-600 text-white" : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      {totalPages}
                    </button>
                  );
                }
              }
              return pages;
            })()}
            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
              className="px-3 py-1 rounded bg-gray-200 disabled:opacity-50 hover:bg-gray-300 transition"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ================= EXISTING ADD/REMOVE MODAL ================= */}
      {showModal && editingCustomer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => setShowModal(false)}
          />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col mx-auto">
            <div className="bg-white border-b border-gray-200 px-6 py-4 rounded-t-lg flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold text-gray-800 mb-0">
                {actionType === "add" ? "Add Points" : "Remove Points"}
              </h2>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <form id="points-form" onSubmit={handlePointsUpdate} className="p-6">
                <div className="space-y-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <Phone size={18} className="text-blue-600" />
                      <span className="font-semibold text-gray-800">
                        {editingCustomer.phoneNumber}
                      </span>
                    </div>
                    <div className="flex items-center justify-center gap-2">
                      <Award size={18} className="text-yellow-500" />
                      <span className="text-lg font-bold text-blue-600">
                        Current: {editingCustomer.bonusWallet?.balance ?? 0} points
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-700">
                      Points to {actionType} *
                    </label>
                    <input
                      type="number"
                      value={points}
                      onChange={(e) => setPoints(e.target.value)}
                      placeholder="Enter points amount"
                      required
                      step="0.01"
                      min="0.01"
                      className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div
                    className={`p-3 rounded-md border ${
                      actionType === "add"
                        ? "bg-green-50 border-green-200"
                        : "bg-orange-50 border-orange-200"
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {actionType === "add" ? (
                        <Plus size={16} className="text-green-600" />
                      ) : (
                        <Minus size={16} className="text-orange-600" />
                      )}
                      <span
                        className={`text-sm font-medium ${
                          actionType === "add" ? "text-green-800" : "text-orange-800"
                        }`}
                      >
                        {actionType === "add"
                          ? "This will add points to the customer's account"
                          : "This will remove points from the customer's account"}
                      </span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="bg-gray-50 border-t border-gray-200 px-6 py-4 rounded-b-lg flex justify-end gap-3 flex-shrink-0">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-5 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="points-form"
                className={`px-5 py-2 text-white rounded-md transition ${
                  actionType === "add"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-orange-600 hover:bg-orange-700"
                }`}
              >
                {actionType === "add" ? "Add Points" : "Remove Points"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* ================= USER DETAILS MODAL ================= */}
      {selectedUser && (
        <UserDetailsModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onBalanceChange={(userId, newBalance) => {
            setCustomers((prev) =>
              prev.map((c) =>
                c._id === userId ? { ...c, bonusWallet: { ...c.bonusWallet, balance: newBalance } } : c
              )
            );
            setSelectedUser((prev) =>
              prev && prev._id === userId
                ? { ...prev, bonusWallet: { ...prev.bonusWallet, balance: newBalance } }
                : prev
            );
          }}
        />
      )}
    </div>
  );
};

export default LoyaltyManagement;
